export const routePaths = {
  root: "/",
  serverError: "/500",
  dashboard: "/dashboard",
  statistics: {
    pl: "/statistics/pl",
  },
  accounting: {
    dashboard: "/accounting/dashboard",
    expenses: "/accounting/expenses",
    funding: "/accounting/funding",
    simulation: "/accounting/simulation",
    configuration: "/accounting/configuration",
  },
  invoicing: {
    generateInvoice: "/invoicing/generate-invoice",
    creditNote: "/invoicing/credit-note",
    debitNote: "/invoicing/debit-note",
    ivaWithholding: "/invoicing/iva-withholding",
    islrWithholding: "/invoicing/islr-withholding",
    retroactiveInvoicing: "/invoicing/retroactive-invoicing",
    retroactiveInvoicingDetail: "/invoicing/retroactive-invoicing/:paymentId",
    configuration: "/invoicing/configuration",
  },
  penalties: {
    list: "/penalties/list",
  },
  profile: "/profile",
  configurations: "/configurations/list",
  roles: {
    list: "/roles/list",
    create: "/roles/create",
    edit: "/roles/edit/:id",
  },
  users: {
    list: "/users/list",
    create: "/users/create",
    edit: "/users/edit/:id",
    confirmEmail: "/users/confirm-email/:userId/:token",
  },
  creditScore: "/credit-score",
  clients: {
    list: "/clients/list",
    missingPhotos: "/clients/missing-photos",
    registerLists: "/clients/register-lists",
    create: "/clients/create",
    edit: "/clients/edit/:id",
    documents: "/clients/documents/list",
  },
  treasury: "/tesoreria/index",
  operations: {
    list: "/operations/list",
    details: "/operations/details/:id",
    payments: "/operations/payments",
    paymentsList: "/operations/payments-list",
    paymentDetail: "/operations/payment/:paymentId",
    refundsList: "/operations/refunds-list",
    refundDetail: "/operations/refund/:refundId",
  },
  creditLine: {
    create: "/credit-line/create",
    list: "/credit-line/list",
    clients: {
      list: "/credit-line/clients/list",
    },
    operations: {
      list: "/credit-line/operations/list",
      details: "/credit-line/operations/details/:id",
      payments: "/credit-line/operations/payments",
      paymentsList: "/credit-line/operations/payments-list",
      paymentDetail: "/credit-line/operations/payment/:paymentId",
      refundsList: "/credit-line/operations/refunds-list",
      refundDetail: "/credit-line/operations/refund/:refundId",
    },
  },
  balance: "/balance",
  fxResults: "/dashboard/fx-results",
  groups: {
    list: "/groups/list",
    create: "/groups/create",
    edit: "/groups/edit/:id",
  },
  notifications: {
    emails: "/notifications/emails",
    list: "/notifications/list",
    create: "/notifications/create",
    deliveries: "/notifications/deliveries",
    inbox: "/notifications/me",
  },
  enterprise: {
    list: "/enterprise/list",
    create: "/enterprise/create",
    edit: "/enterprise/edit/:id",
    uploadPayroll: "/enterprise/upload-payroll/:id",
  },
  rateHistory: "/rate-history",
  logout: "/logout",
  auth: {
    login: "/login",
    logout: "/logout",
    register: "/register",
    recoverPassword: "/recoverpw",
    pageLogin: "/page-login",
    pageRegister: "/page-register",
    pageRecoverPassword: "/page-recoverpw",
    pageLockScreen: "/page-lock-screen",
    pageConfirmMail: "/page-confirm-mail",
    pageEmailVerification: "/page-email-verification",
    pageTwoStepVerification: "/page-two-step-verification",
    pageLogout: "/page-logout",
  },
  utility: {
    comingSoon: "/pages-comingsoon",
    maintenance: "/pages-maintenance",
    notFound: "/pages-404",
  },
} as const;

export type SidebarNavigationItem = {
  label: string;
  path?: string;
  icon?: string;
  modules: string[];
  excludedRoles?: string[];
  translationKey?: string;
  separatorBefore?: boolean;
  children?: SidebarNavigationItem[];
};

export const sidebarNavigationItems: SidebarNavigationItem[] = [
  {
    label: "Dashboards",
    icon: "bar-chart-2",
    modules: ["dashboard", "accounting"],
    children: [
      {
        label: "Inicio",
        path: routePaths.dashboard,
        icon: "home",
        modules: ["dashboard"],
      },
      {
        label: "P&L",
        path: routePaths.statistics.pl,
        icon: "trending-up",
        modules: ["dashboard", "accounting"],
      },
      {
        label: "Balance",
        path: routePaths.balance,
        icon: "credit-card",
        modules: ["dashboard", "accounting"],
      },
      {
        label: "Resultado cambiario",
        path: routePaths.fxResults,
        icon: "repeat",
        modules: ["dashboard", "accounting"],
      },
    ],
  },
  {
    label: "Contabilidad",
    icon: "book-open",
    modules: ["accounting"],
    children: [
      {
        label: "Dashboard",
        path: routePaths.accounting.dashboard,
        icon: "bar-chart-2",
        modules: ["accounting"],
      },
      {
        label: "Gastos",
        path: routePaths.accounting.expenses,
        icon: "file-text",
        modules: ["accounting"],
      },
      {
        label: "Fondeo",
        path: routePaths.accounting.funding,
        icon: "dollar-sign",
        modules: ["accounting"],
      },
      {
        label: "Simulación",
        path: routePaths.accounting.simulation,
        icon: "activity",
        modules: ["accounting"],
      },
      {
        label: "Configuracion",
        path: routePaths.accounting.configuration,
        icon: "settings",
        modules: ["accounting"],
      },
    ],
  },
  {
    label: "Facturación",
    icon: "file-text",
    modules: ["accounting"],
    children: [
      {
        label: "Facturas",
        path: routePaths.invoicing.generateInvoice,
        icon: "clipboard",
        modules: ["accounting"],
      },
      {
        label: "Nota de Crédito",
        path: routePaths.invoicing.creditNote,
        icon: "file-minus",
        modules: ["accounting"],
      },
      {
        label: "Nota de Débito",
        path: routePaths.invoicing.debitNote,
        icon: "file-plus",
        modules: ["accounting"],
      },
      {
        label: "Retención IVA",
        path: routePaths.invoicing.ivaWithholding,
        icon: "percent",
        modules: ["accounting"],
      },
      {
        label: "Retención ISLR",
        path: routePaths.invoicing.islrWithholding,
        icon: "percent",
        modules: ["accounting"],
      },
      {
        label: "Facturación Retroactiva",
        path: routePaths.invoicing.retroactiveInvoicing,
        icon: "rotate-ccw",
        modules: ["accounting"],
      },
      {
        label: "Configuración",
        path: routePaths.invoicing.configuration,
        icon: "settings",
        modules: ["accounting"],
      },
    ],
  },
  {
    label: "Configuraciones",
    icon: "shield",
    modules: ["generals", "roles", "users", "credit-score"],
    children: [
      {
        label: "Roles",
        translationKey: "Roles",
        path: routePaths.roles.list,
        modules: ["roles"],
      },
      {
        label: "Usuarios",
        path: routePaths.users.list,
        modules: ["users"],
      },
      {
        label: "Credit Score",
        translationKey: "Credit Score",
        path: routePaths.creditScore,
        modules: ["credit-score"],
      },
    ],
  },
  {
    label: "Histórico tasa BCV",
    path: routePaths.rateHistory,
    icon: "book-open",
    modules: ["rates"],
  },
  {
    label: "Clientes",
    icon: "users",
    modules: ["clients", "clients-view", "register-list"],
    separatorBefore: true,
    children: [
      {
        label: "Listado",
        path: routePaths.clients.list,
        modules: ["clients", "clients-view"],
      },
      {
        label: "Foto faltante",
        path: routePaths.clients.missingPhotos,
        icon: "image",
        modules: ["clients"],
      },
      {
        label: "Listas Registro",
        path: routePaths.clients.registerLists,
        icon: "list",
        modules: ["register-list"],
      },
    ],
  },
  {
    label: "Operaciones",
    icon: "dollar-sign",
    modules: ["operation-requests", "operation-payments"],
    excludedRoles: ["soporte"],
    children: [
      {
        label: "Solicitudes de crédito",
        path: routePaths.operations.list,
        modules: ["operation-requests"],
      },
      {
        label: "Cuotas",
        path: routePaths.operations.payments,
        modules: ["operation-payments"],
      },
      {
        label: "Pagos",
        path: routePaths.operations.paymentsList,
        modules: ["operation-payments"],
      },
      {
        label: "Reembolsos",
        path: routePaths.operations.refundsList,
        modules: ["operation-payments"],
      },
    ],
  },
  {
    label: "Línea de Crédito",
    icon: "briefcase",
    modules: ["credit-line", "credit-line-view", "accounting"],
    separatorBefore: true,
    children: [
      {
        label: "Crear",
        path: routePaths.creditLine.create,
        icon: "plus-circle",
        modules: ["credit-line"],
      },
      {
        label: "Listado",
        path: routePaths.creditLine.list,
        icon: "list",
        modules: ["credit-line"],
      },
      {
        label: "Clientes",
        icon: "users",
        modules: ["credit-line", "credit-line-view", "accounting"],
        children: [
          {
            label: "Listado",
            path: routePaths.creditLine.clients.list,
            modules: ["credit-line", "credit-line-view", "accounting"],
          },
        ],
      },
      {
        label: "Operaciones",
        icon: "dollar-sign",
        modules: ["credit-line", "credit-line-view", "accounting"],
        children: [
          {
            label: "Solicitudes de crédito",
            path: routePaths.creditLine.operations.list,
            modules: ["credit-line", "credit-line-view", "accounting"],
          },
          {
            label: "Cuotas",
            path: routePaths.creditLine.operations.payments,
            modules: ["credit-line", "credit-line-view", "accounting"],
          },
          {
            label: "Pagos",
            path: routePaths.creditLine.operations.paymentsList,
            modules: ["credit-line", "credit-line-view", "accounting"],
          },
          {
            label: "Reembolsos",
            path: routePaths.creditLine.operations.refundsList,
            modules: ["credit-line", "credit-line-view", "accounting"],
          },
        ],
      },
    ],
  },
  {
    label: "Grupos",
    icon: "users",
    modules: ["notifications"],
    separatorBefore: true,
    children: [
      {
        label: "Listado",
        path: routePaths.groups.list,
        modules: ["notifications"],
      },
      {
        label: "Crear",
        path: routePaths.groups.create,
        modules: ["notifications"],
      },
    ],
  },
  {
    label: "Notificaciones",
    icon: "bell",
    modules: ["notifications"],
    children: [
      {
        label: "Emails",
        path: routePaths.notifications.emails,
        icon: "mail",
        modules: ["notifications"],
      },
      {
        label: "Campañas",
        path: routePaths.notifications.list,
        modules: ["notifications"],
      },
      {
        label: "Crear",
        path: routePaths.notifications.create,
        modules: ["notifications"],
      },
      {
        label: "Entregas",
        path: routePaths.notifications.deliveries,
        modules: ["notifications"],
      },
    ],
  },
  {
    label: "Penalidades",
    path: routePaths.penalties.list,
    icon: "alert-triangle",
    modules: ["dashboard"],
  },
];
